const truncateStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '4px 2px'
};

export const tableColumns = [
    {
        name: 'Quiz ID',
        selector: row => row.quiz_id,
        sortable: true,
        center: true,
        width: '100px',
        style: truncateStyle,
    },
    {
        name: 'Title',
        selector: row => row.title,
        sortable: true,
        center: true,
        width: '200px',
        cell: row => (
            <div style={{
                ...truncateStyle,
                color: '#5C61F2',
                fontWeight: 'bold',
                width: '100%',
                textAlign: 'center'
            }}>
                {row.title}
            </div>
        ),
    },
    {
        name: 'Category',
        selector: row => row.category,
        sortable: true,
        center: true,
        width: '140px',
        style: truncateStyle,
    },
    {
        name: 'Topic',
        selector: row => row.topic,
        sortable: true,
        center: true,
        width: '150px',
        style: truncateStyle,
    },
    {
        name: 'Mapped To',
        selector: row => row.mapping_count,
        sortable: true,
        center: true,
        width: '110px',
        style: truncateStyle,
    },
    {
        name: 'Questions',
        selector: row => row.question_count,
        sortable: true,
        center: true,
        width: '110px',
        style: truncateStyle,
    },
    {
        name: 'Levels',
        selector: row => row.level_count,
        sortable: true,
        center: true,
        width: '90px',
        style: truncateStyle,
    },
    {
        name: 'Created Date',
        selector: row => row.created_date,
        sortable: true,
        center: true,
        width: '140px',
        style: truncateStyle,
    },
    {
        name: 'Status',
        selector: row => row.status_component,
        sortable: false,
        center: true,
        width: '100px',
        id: 'status',
        style: truncateStyle,
    },
    {
        name: 'Action',
        selector: row => row.action,
        sortable: false,
        center: true,
        width: '160px',
        id: 'action',
        style: truncateStyle,
    }
];

/* ------------------------------------------------------------------ *
 * Allowed values. The CODE is what the API expects; the label is what
 * the admin sees.
 * ------------------------------------------------------------------ */

export const QUIZ_CATEGORY_OPTIONS = [
    { value: 'ARTIFACT', label: 'Artifact' },
    { value: 'QUIZ_SECTION', label: 'Quiz Section' },
    { value: 'COURSE', label: 'Courses' }
];

export const TOPIC_OPTIONS = [
    { value: 'PHILOSOPHY', label: 'Philosophy' },
    { value: 'SCRIPTURES', label: 'Scriptures' },
    { value: 'TEERTHAS_OF_INDIA', label: 'Teerthas of India' },
    { value: 'SPIRITUAL_INSIGHTS', label: 'Spiritual Insights' },
    { value: 'KIDS_CORNER', label: 'Kids Corner' },
    { value: 'MANTRAS', label: 'Mantras' }
];

export const STATUS_OPTIONS = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' }
];

export const ARTIFACT_TYPE_OPTIONS = [
    { value: 'READ', label: 'Read' },
    { value: 'LISTEN', label: 'Listen' },
    { value: 'WATCH', label: 'Watch' }
];

export const TIMER_OPTIONS = [
    { value: 20, label: '20 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 50, label: '50 seconds' },
    { value: 60, label: '1 minute' }
];

// options[0] is A, [1] is B, [2] is C, [3] is D - the API stores the LETTER.
export const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

export const MAX_LEVELS = 5;

// Suffixes match the API columns: question / question_hin / question_mar / question_guj
export const QUIZ_LANGUAGES = [
    { key: 'en', suffix: '', name: 'English', short: 'Eng' },
    { key: 'hi', suffix: '_hin', name: 'Hindi', short: 'Hin' },
    { key: 'mr', suffix: '_mar', name: 'Marathi', short: 'Mar' },
    { key: 'gu', suffix: '_guj', name: 'Gujarati', short: 'Guj' }
];

export const getCategoryLabel = (value) =>
    QUIZ_CATEGORY_OPTIONS.find(option => option.value === value)?.label || value || '-';

export const getTopicLabel = (value) =>
    TOPIC_OPTIONS.find(option => option.value === value)?.label || value || '-';

export const getStatusLabel = (value) =>
    STATUS_OPTIONS.find(option => option.value === value)?.label || value || '-';

export const getStatusColor = (status) => ({
    DRAFT: '#FFC107',
    ACTIVE: '#00C853',
    INACTIVE: '#8B5CF6'
}[status] || '#8B5CF6');

/* ------------------------------------------------------------------ *
 * Question helpers
 * ------------------------------------------------------------------ */

let questionKeySeed = 0;
const nextQuestionKey = () => `q_${Date.now()}_${questionKeySeed++}`;

export const createEmptyQuestion = (levelNumber = 1) => ({
    key: nextQuestionKey(),
    id: null,                 // server id, null until the question has been saved
    level_number: levelNumber,
    sequence_number: null,
    question: '',
    question_hin: '',
    question_mar: '',
    question_guj: '',
    options: ['', '', '', ''],
    options_hin: ['', '', '', ''],
    options_mar: ['', '', '', ''],
    options_guj: ['', '', '', ''],
    correct_option: '',
    dirty: true
});

// Options can arrive as a parsed array; be tolerant of a JSON string too.
const toOptionArray = (value) => {
    let source = value;
    if (typeof source === 'string') {
        try {
            source = JSON.parse(source);
        } catch (error) {
            source = null;
        }
    }
    const padded = ['', '', '', ''];
    if (Array.isArray(source)) {
        source.slice(0, 4).forEach((item, index) => {
            padded[index] = item == null ? '' : String(item);
        });
    }
    return padded;
};

export const normalizeQuestionFromApi = (question) => ({
    key: nextQuestionKey(),
    id: question.id,
    level_number: Number(question.level_number) || 1,
    sequence_number: question.sequence_number ?? null,
    question: question.question || '',
    question_hin: question.question_hin || '',
    question_mar: question.question_mar || '',
    question_guj: question.question_guj || '',
    options: toOptionArray(question.options),
    options_hin: toOptionArray(question.options_hin),
    options_mar: toOptionArray(question.options_mar),
    options_guj: toOptionArray(question.options_guj),
    correct_option: question.correct_option || '',
    dirty: false
});

// A localized option set is sent only when all four entries are filled in.
// Partly filled sets are rejected by validateQuestion, and an empty set is
// omitted so the app keeps falling back to English.
const localizedOptions = (options) => {
    const trimmed = (options || []).map(option => (option || '').trim());
    if (trimmed.length !== 4) return null;
    return trimmed.every(option => option.length > 0) ? trimmed : null;
};

const localizedText = (value) => {
    const trimmed = (value || '').trim();
    return trimmed.length > 0 ? trimmed : null;
};

export const buildQuestionPayload = (question) => ({
    level_number: Number(question.level_number) || 1,
    question: (question.question || '').trim(),
    question_hin: localizedText(question.question_hin),
    question_mar: localizedText(question.question_mar),
    question_guj: localizedText(question.question_guj),
    options: (question.options || []).map(option => (option || '').trim()),
    options_hin: localizedOptions(question.options_hin),
    options_mar: localizedOptions(question.options_mar),
    options_guj: localizedOptions(question.options_guj),
    correct_option: question.correct_option
});

/**
 * Mirrors the server rules so the admin gets feedback before the request.
 * Returns an error string, or null when the question is valid.
 */
export const validateQuestion = (question, label) => {
    const prefix = label || 'Question';

    if (!(question.question || '').trim()) {
        return `${prefix}: question text is required`;
    }

    const options = (question.options || []).map(option => (option || '').trim());
    if (options.length !== 4 || options.some(option => !option)) {
        return `${prefix}: all 4 options (A-D) are required`;
    }

    if (!OPTION_LETTERS.includes(question.correct_option)) {
        return `${prefix}: please select the correct answer`;
    }

    const levelNumber = Number(question.level_number);
    if (!levelNumber || levelNumber < 1 || levelNumber > MAX_LEVELS) {
        return `${prefix}: level must be between 1 and ${MAX_LEVELS}`;
    }

    // Localized option sets are all-or-nothing.
    for (const language of QUIZ_LANGUAGES.filter(item => item.suffix)) {
        const raw = (question[`options${language.suffix}`] || []).map(option => (option || '').trim());
        const filled = raw.filter(option => option.length > 0).length;
        if (filled > 0 && filled < 4) {
            return `${prefix}: fill all 4 ${language.name} options or leave them all empty`;
        }
    }

    return null;
};

/* ------------------------------------------------------------------ *
 * Mapping helpers
 * ------------------------------------------------------------------ */

/**
 * Builds the mappings array for the selected placement.
 * QUIZ_SECTION never has mappings, COURSE has exactly one (course OR chapter),
 * ARTIFACT has one or more, all of entity_type ARTIFACT.
 */
export const buildMappings = (quizCategory, { selectedArtifacts = [], courseId = '', chapterId = '' } = {}) => {
    if (quizCategory === 'ARTIFACT') {
        return selectedArtifacts.map(artifact => ({
            entity_type: 'ARTIFACT',
            entity_id: artifact.artifact_id
        }));
    }

    if (quizCategory === 'COURSE') {
        if (chapterId) {
            return [{ entity_type: 'CHAPTER', entity_id: chapterId }];
        }
        if (courseId) {
            return [{ entity_type: 'COURSE', entity_id: courseId }];
        }
        return [];
    }

    return [];
};

/**
 * Validates the quiz details section. Returns an error string or null.
 */
export const validateQuizDetails = (formData, { selectedArtifacts = [], courseId = '' } = {}) => {
    if (!(formData.title || '').trim()) {
        return 'Quiz title is required';
    }

    if (!formData.quiz_category) {
        return 'Quiz category is required';
    }

    if (formData.timer_enabled && !formData.time_per_question_seconds) {
        return 'Please select the time per question';
    }

    if (formData.quiz_category === 'QUIZ_SECTION') {
        if (formData.status === 'ACTIVE' && !formData.topic) {
            return 'A topic is required before a Quiz Section quiz can be set to Active';
        }
    }

    if (formData.quiz_category === 'ARTIFACT') {
        if (!formData.artifact_type) {
            return 'Please select an artifact type';
        }
        if (selectedArtifacts.length === 0) {
            return 'Please select at least one artifact for this quiz';
        }
    }

    if (formData.quiz_category === 'COURSE' && !courseId) {
        return 'Please select a course';
    }

    return null;
};

/**
 * 409 means the artifact/course/chapter already has a quiz attached - the
 * server message is the useful one, so surface it as-is.
 */
export const getApiErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

export const isConflictError = (error) => error?.response?.status === 409;

export const isNotFoundError = (error) => error?.response?.status === 404;
